import React, { useState } from 'react';

import { toast } from 'react-toastify';

import { ArrowLeftRight, Eye, EyeOff, X } from 'lucide-react';

import {
    LocationState,
    useUpdateLocationsStateMutation,
    useSwapProductLocationsMutation,
    TotadminPermission,
} from 'data/__generated__';

import { ActionBar } from 'components/ActionBar';

import { handleUpdateActionEndToast } from 'pages/SupplyOrders/shared/handleUpdateActionEndToast';
import { LocationToPrint, PrintLocationTags } from './PrintLocationTags';

export const LocationActions = ({
    selectedLocationIds,
    setSelectedLocationIds,
    regularLocationsToPrint,
}: {
    selectedLocationIds: string[];
    setSelectedLocationIds: (locationIds: string[]) => void;
    regularLocationsToPrint: LocationToPrint[];
}) => {
    const [stateMutationLoading, setStateMutationLoading] = useState<LocationState | null>(null);
    const [updateLocationsState] = useUpdateLocationsStateMutation();
    const [swapProductLocations, { loading: swapLoading }] = useSwapProductLocationsMutation();

    const handleStateUpdate = async (stateToSet: LocationState) => {
        setStateMutationLoading(stateToSet);
        const { data } = await updateLocationsState({
            variables: {
                locationIds: selectedLocationIds,
                stateToSet,
            },
        });

        setSelectedLocationIds([]);
        if (data) {
            const {
                updateLocationsState: { error, warnings },
            } = data;

            setStateMutationLoading(null);
            handleUpdateActionEndToast({
                errors: error ? [error] : [],
                warnings: warnings || [],
                successText: 'Les statuts ont été mis à jour !',
                displaySuccess: !error && warnings?.length !== selectedLocationIds.length,
            });
        } else {
            toast.error('Une erreur inconnue est survenue');
        }
    };

    const handleProductSwap = async () => {
        if (selectedLocationIds.length !== 2) {
            toast.info('Veuillez sélectionner exactement 2 emplacements');
            return;
        }

        const { data } = await swapProductLocations({
            variables: { locationIds: selectedLocationIds },
        });

        setSelectedLocationIds([]);
        if (data) {
            const {
                swapProductLocations: { warnings, error },
            } = data;

            handleUpdateActionEndToast({
                errors: error ? [error] : [],
                warnings: warnings || [],
                successText:
                    "Les emplacements des produits ont été échangés ! N'oubliez pas d'échanger le stock physique",
                displaySuccess: !(error || warnings?.length),
            });
        } else {
            toast.error('Une erreur inconnue est survenue');
        }
    };

    const actionSections = [
        {
            actions: [
                {
                    icon: <X />,
                    label: 'Tout désélectionner',
                    onClick: () => {
                        setSelectedLocationIds([]);
                    },
                    hidden: !selectedLocationIds.length,
                },
                {
                    icon: <Eye />,
                    label: 'Passer en Actif',
                    requiredPermissions: [TotadminPermission.WarehouseLocationsManage],
                    onClick: () => handleStateUpdate(LocationState.Regular),
                    loading: stateMutationLoading === LocationState.Regular,
                    hidden: stateMutationLoading === LocationState.Hidden || !selectedLocationIds.length,
                },
                {
                    icon: <EyeOff />,
                    label: 'Passer en Caché',
                    requiredPermissions: [TotadminPermission.WarehouseLocationsManage],
                    onClick: () => handleStateUpdate(LocationState.Hidden),
                    loading: stateMutationLoading === LocationState.Hidden,
                    hidden: stateMutationLoading === LocationState.Regular || !selectedLocationIds.length,
                },
                {
                    icon: <ArrowLeftRight />,
                    label: 'Échanger produits',
                    requiredPermissions: [TotadminPermission.WarehouseLocationsManage],
                    onClick: handleProductSwap,
                    loading: swapLoading,
                    hidden: !selectedLocationIds.length,
                },
            ],
        },
    ];

    return (
        <ActionBar actionSections={actionSections}>
            <PrintLocationTags locationsToPrint={regularLocationsToPrint} />
        </ActionBar>
    );
};
